import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { cAPI } from '../../../utils/cAPI';
import styles from './index.module.scss';

const authorizationKeys =
[
    'client_id',
    'response_type',
    'redirect_uri',
    'state',
    'code_challenge',
    'code_challenge_method'
];

const ConnecticoHubAuthorize = () =>
{
    const location = useLocation();
    const [requestDetails, setRequestDetails] = useState(null);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const authorizationQuery = useMemo(() =>
    {
        const source = new URLSearchParams(location.search);
        const target = new URLSearchParams();

        authorizationKeys.forEach(key =>
        {
            const value = source.get(key);
            if (value)
                target.set(key, value);
        });

        return target;
    }, [location.search]);

    useEffect(() =>
    {
        let isCurrent = true;

        const load = async () =>
        {
            try
            {
                const response = await cAPI.APGetHubOAuthAuthorizationRequest(authorizationQuery.toString());

                if (!isCurrent)
                    return;

                if (response.payLoad)
                    setRequestDetails(response.payLoad);
                else
                    setError(response.error?.message || response.unhandledError?.message || 'Nie udało się zweryfikować żądania połączenia.');
            }
            catch (exception)
            {
                if (isCurrent)
                    setError(exception?.message || 'Nie udało się zweryfikować żądania połączenia.');
            }
        };

        load();
        return () => { isCurrent = false; };
    }, [authorizationQuery]);

    const decide = async approved =>
    {
        setIsSubmitting(true);
        setError(null);

        try
        {
            const request = Object.fromEntries(authorizationQuery.entries());
            const response = await cAPI.APAuthorizeHubOAuth
            ({
                approved,
                clientId: request.client_id,
                responseType: request.response_type,
                redirectUri: request.redirect_uri,
                state: request.state,
                codeChallenge: request.code_challenge,
                codeChallengeMethod: request.code_challenge_method
            });

            if (response.payLoad?.redirectUri)
            {
                window.location.assign(response.payLoad.redirectUri);
                return;
            }

            setError(response.error?.message || response.unhandledError?.message || 'Nie udało się zakończyć autoryzacji.');
        }
        catch (exception)
        {
            setError(exception?.message || 'Nie udało się zakończyć autoryzacji.');
        }
        finally
        {
            setIsSubmitting(false);
        }
    };

    return (
        <main className={styles.page}>
            <section className={styles.card} aria-labelledby="hub-oauth-title">
                <div className={styles.brand}>connectico</div>

                {error ? (
                    <div className={styles.error} role="alert">
                        <h1 id="hub-oauth-title">Nie można połączyć huba</h1>
                        <p>{error}</p>
                    </div>
                ) : !requestDetails ? (
                    <div className={styles.loading}>Sprawdzanie żądania połączenia…</div>
                ) : (
                    <>
                        <div className={styles.iconRow} aria-hidden="true">
                            <div className={styles.shopIcon}>S</div>
                            <div className={styles.connector} />
                            <div className={styles.hubIcon}>H</div>
                        </div>

                        <h1 id="hub-oauth-title">Połącz sklep z Connectico Hub</h1>
                        <p className={styles.lead}>
                            Aplikacja <strong>{requestDetails.clientName}</strong> prosi o dostęp do sklepu <strong>{requestDetails.shopName}</strong>.
                        </p>

                        <div className={styles.permissions}>
                            <div className={styles.permissionsTitle}>Hub będzie mógł:</div>
                            <ul>
                                {requestDetails.permissions.map(permission => (
                                    <li key={permission}>{permission}</li>
                                ))}
                            </ul>
                        </div>

                        <p className={styles.hint}>
                            Hasło administratora pozostanie w sklepie. Dostęp jest związany z tym kontem i wygaśnie automatycznie.
                        </p>

                        <div className={styles.actions}>
                            <button type="button" className={styles.secondaryButton} disabled={isSubmitting} onClick={() => decide(false)}>
                                Anuluj
                            </button>
                            <button type="button" className={styles.primaryButton} disabled={isSubmitting} onClick={() => decide(true)}>
                                {isSubmitting ? 'Łączenie…' : 'Połącz z hubem'}
                            </button>
                        </div>
                    </>
                )}
            </section>
        </main>
    );
};

export default ConnecticoHubAuthorize;
